// Discord-specific Multiplayer game logic
// Uses the Worker API instead of direct Firestore access

import { gameAPI, getHeaders } from './api.js';
import { Game } from './game.js';
import { isValidWord } from './dictionary.js';
import { setLayoutByName } from './board.js';
import { updateActivityState } from './discord.js';
import {
    MOVE_REASON,
    isPlayerActive,
    getTurnsTaken,
    canPlayerMove,
    getReasonMessage
} from '@wordsheet/shared';
import { flags } from './flags.js';
import { celebrate, startAmbientFireworks } from './celebration.js';

function getTurnTimeoutMs() {
    return flags.get('autoAwayMinutes') * 60 * 1000;
}

export class DiscordMultiplayerGame {
    constructor(gameCode, playerId, nickname) {
        this.gameCode = gameCode;
        this.playerId = playerId;
        this.nickname = nickname;

        this.localGame = null;
        this.gameState = null;
        this.isMyTurn = false;
        this.wasMyTurn = false; // Track previous state for notifications
        this.isSubmitting = false;
        this.isCheckingTimeout = false;
        this.isCheckingAllIdle = false;
        this.lastBoardSignature = null;
        this.lastRackSignature = null;
        this.audioContext = null; // For notification sounds
        this.softGameOverActive = false;
        this.lastLocalActivity = Date.now(); // Track local activity to prevent false auto-away
        this.cleanupAmbientFireworks = null;

        // UI elements
        this.playerListEl = document.getElementById('game-players');
        this.moveHistoryEl = document.getElementById('move-history');
        this.messageEl = document.getElementById('message');
        this.controlsEl = document.querySelector('.controls');
    }

    async init() {
        // Get initial game state
        this.gameState = await gameAPI.getGame(this.gameCode);
        if (!this.gameState) {
            throw new Error('Game not found');
        }

        // Create local game instance
        this.localGame = new Game({ isMultiplayer: true, skipTileBag: true });

        // Override methods
        this.localGame.submitWord = () => this.submitMove();
        this.localGame.executeSwap = () => this.submitSwap();
        this.localGame.skipTurn = () => {
            if (this.isCurrentPlayerIdle()) {
                this.comeBackFromAway();
            } else {
                this.snooze();
            }
        };
        this.localGame.finishGame = () => this.finishGame();

        const originalEnterSwapMode = this.localGame.enterSwapMode.bind(this.localGame);
        this.localGame.enterSwapMode = () => {
            if (!this.isMyTurn) {
                this.showMessage("It's not your turn!", 'error');
                return;
            }
            if (this.gameState.tileBag.length === 0) {
                this.showMessage('No tiles left in the bag!', 'error');
                return;
            }
            originalEnterSwapMode();
        };

        this.localGame.updateTileBagCounter = () => {};
        this.localGame.updateEndgameButtons = () => {};

        // Load initial state
        this.loadBoardState();
        this.loadRackState();
        this.updateTurnState();
        this.renderPlayers();
        this.renderMoveHistory();
        this.updateTileBagCounter();

        // Start polling for updates
        this.startPolling();

        // Listen for any user activity to return from away mode
        this.setupActivityListeners();

        // Apply initial game flags if present
        if (this.gameState.gameFlags) {
            flags.applyGameFlags(this.gameState.gameFlags);
        }

        // Sync flag changes to worker API when admin panel is used
        flags.onFlagChanged = (flag, value) => {
            if (!this.gameCode || this.gameState?.status !== 'active') return;
            const body = flag === '__reset' ? value : { [flag]: value };
            fetch(`/.proxy/api/games/${this.gameCode}/flags`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify(body)
            }).catch(e => console.warn('Failed to sync flag:', e));
        };
    }

    setupActivityListeners() {
        const gameContainer = document.querySelector('.game-container') || document.body;

        const onActivity = () => {
            // Track local activity to prevent false auto-away timeouts
            // This ensures any interaction (tile picker, board clicks, etc.) resets the timer
            this.lastLocalActivity = Date.now();

            // Check if current player is idle and bring them back
            if (this.isCurrentPlayerIdle()) {
                this.comeBackFromAway();
            }
        };

        // Listen for clicks and key presses anywhere in the game
        gameContainer.addEventListener('click', onActivity);
        gameContainer.addEventListener('keydown', onActivity);

        // Store references for cleanup
        this.activityHandler = onActivity;
        this.activityContainer = gameContainer;
    }

    isCurrentPlayerIdle() {
        if (!this.gameState || !this.gameState.players) return false;
        const player = this.gameState.players.find(p => p.id === this.playerId);
        return player?.isIdle === true;
    }

    startPolling() {
        gameAPI.startPolling(this.gameCode, (game) => {
            const stateChanged = JSON.stringify(this.gameState) !== JSON.stringify(game);
            if (stateChanged) {
                this.gameState = game;
                this.onGameStateUpdate();
            }
        }, 1000);
    }

    onGameStateUpdate() {
        // Apply synced game flags before anything reads them
        if (this.gameState.gameFlags) {
            flags.applyGameFlags(this.gameState.gameFlags);
        }

        const boardSignature = this.getBoardSignature(this.gameState.boardTiles || []);
        if (boardSignature !== this.lastBoardSignature) {
            this.loadBoardState();
        }

        // Only reload rack if player doesn't have pending tiles on the board
        // (to avoid overwriting their in-progress move)
        const pendingTiles = this.localGame.board.getPendingTiles();
        if (pendingTiles.length === 0) {
            this.loadRackState();
        }

        this.updateTurnState();
        this.renderPlayers();
        this.renderMoveHistory();
        this.updateTileBagCounter();

        // Check for game end or soft game over
        if (this.gameState.status === 'ended') {
            if (this.softGameOverActive) {
                this.upgradeSoftToTrueGameOver();
            } else {
                this.showGameOver();
            }
            return;
        } else if (this.softGameOverActive) {
            this.refreshSoftScoreboard();
        } else if (this.shouldShowSoftGameOver()) {
            this.showSoftGameOver();
        }

        // Check if current player has timed out
        this.checkTurnTimeout();

        // Check if all remaining players are idle and auto-end if needed
        this.checkAllIdleAndAutoEnd();
    }

    async checkAllIdleAndAutoEnd() {
        if (this.isCheckingAllIdle || this.gameState.status === 'ended') {
            return;
        }

        // Check if all remaining active players are idle
        const activePlayers = this.gameState.players.filter(p => isPlayerActive(p));
        if (activePlayers.length === 0) {
            return; // Game should already be ended
        }

        const allAreIdle = activePlayers.every(p => p.isIdle);
        if (!allAreIdle || !flags.get('autoEndWhenAllIdle')) {
            return;
        }

        // All remaining active players are idle - call server to auto-end
        this.isCheckingAllIdle = true;

        try {
            const response = await fetch(`/.proxy/api/games/${this.gameCode}/auto-end-idle`, {
                method: 'POST',
                headers: getHeaders()
            });

            if (response.ok) {
                const { game, ended } = await response.json();
                if (ended) {
                    this.gameState = game;
                    this.onGameStateUpdate();
                }
            }
        } catch (error) {
            console.error('Failed to auto-end idle game:', error);
        } finally {
            this.isCheckingAllIdle = false;
        }
    }

    loadBoardState() {
        // Set the board layout from game state (must happen before initBoard)
        // Fallback to "Ring Pattern" for games created before layoutName was added
        const layoutName = this.gameState.layoutName || 'Ring Pattern';
        setLayoutByName(layoutName);

        // Save pending tiles before the snapshot wipes them
        const savedPending = this.localGame.board.getPendingTiles();

        const tiles = this.gameState.boardTiles || [];
        this.localGame.board.loadSnapshot(tiles);
        this.localGame.isFirstMove = this.gameState.isFirstMove;
        this.lastBoardSignature = this.getBoardSignature(tiles);

        // Restore pending tiles that don't conflict with new locked tiles
        for (const { row, col, tile } of savedPending) {
            if (this.localGame.board.cells[row][col].locked) {
                // Conflict: another player placed a tile here — return to rack
                if (tile.isBlank) { tile.assignedLetter = null; }
                this.localGame.rack.push(tile);
            } else {
                this.localGame.board.placeTile(row, col, tile, true);
            }
        }

        if (savedPending.length > 0) {
            this.localGame.renderRack();
        }
    }

    getBoardSignature(tiles) {
        if (!tiles || tiles.length === 0) return '';
        return tiles
            .map(t => `${t.row},${t.col},${t.id},${t.assignedLetter || ''}`)
            .sort()
            .join('|');
    }

    getRackSignature(rack) {
        if (!rack || rack.length === 0) return '';
        // Sort by ID to detect tile changes regardless of order
        return rack
            .map(t => t.id)
            .sort()
            .join(',');
    }

    loadRackState() {
        const myPlayer = this.gameState.players.find(p => p.id === this.playerId);
        if (myPlayer && myPlayer.rack) {
            const newRackSignature = this.getRackSignature(myPlayer.rack);
            // Only reload rack if the tiles themselves have changed
            // (not just because of a poll update with same tiles)
            if (newRackSignature !== this.lastRackSignature) {
                this.localGame.rack = myPlayer.rack.map(t => ({
                    letter: t.letter,
                    points: t.points,
                    isBlank: t.isBlank,
                    id: t.id,
                    assignedLetter: t.assignedLetter || null
                }));
                this.localGame.renderRack();
                this.lastRackSignature = newRackSignature;
            }
        }

        if (myPlayer) {
            this.localGame.score = myPlayer.score;
            document.getElementById('score').textContent = myPlayer.score;
        }
    }

    // Note: isPlayerActive, getTurnsTaken, canPlayerMove are imported from @wordsheet/shared

    // Check and enforce turn timeout
    async checkTurnTimeout() {
        if (this.isCheckingTimeout || this.gameState.status === 'ended') {
            return;
        }

        const currentPlayer = this.gameState.players[this.gameState.currentTurnIndex];
        if (!currentPlayer || !isPlayerActive(currentPlayer) || currentPlayer.isIdle) {
            return;
        }

        // For the local player, use the most recent of server lastActiveAt and local activity
        // This prevents false auto-away when player is actively interacting (tile picker, etc.)
        let lastActive = Math.max(currentPlayer.lastActiveAt || 0, this.gameState.startedAt || 0);
        if (currentPlayer.id === this.playerId && this.lastLocalActivity) {
            lastActive = Math.max(lastActive, this.lastLocalActivity);
        }
        const elapsed = Date.now() - lastActive;

        if (elapsed < getTurnTimeoutMs()) {
            return;
        }

        // Current player has timed out - call server to handle it
        this.isCheckingTimeout = true;

        try {
            const response = await fetch(`/.proxy/api/games/${this.gameCode}/timeout`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify({
                    autoEndWhenAllIdle: flags.get('autoEndWhenAllIdle')
                })
            });

            if (response.ok) {
                const { game, skipped } = await response.json();
                if (skipped) {
                    this.gameState = game;
                    this.onGameStateUpdate();
                }
            }
        } catch (error) {
            console.error('Failed to check timeout:', error);
        } finally {
            this.isCheckingTimeout = false;
        }
    }

    // Play a notification sound using Web Audio API
    playNotificationSound() {
        try {
            if (!this.audioContext) {
                this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
            }
            const ctx = this.audioContext;

            // Create a pleasant two-tone chime
            const playTone = (freq, startTime, duration) => {
                const oscillator = ctx.createOscillator();
                const gainNode = ctx.createGain();

                oscillator.connect(gainNode);
                gainNode.connect(ctx.destination);

                oscillator.frequency.value = freq;
                oscillator.type = 'sine';

                gainNode.gain.setValueAtTime(0.3, startTime);
                gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);

                oscillator.start(startTime);
                oscillator.stop(startTime + duration);
            };

            const now = ctx.currentTime;
            playTone(587.33, now, 0.15);        // D5
            playTone(880, now + 0.15, 0.2);     // A5
        } catch (e) {
            console.log('Could not play notification sound:', e);
        }
    }

    updateTurnState() {
        if (this.isSubmitting) {
            this.controlsEl.classList.add('disabled');
            return;
        }

        // Use shared canPlayerMove function
        const moveStatus = canPlayerMove(
            this.gameState.players,
            this.gameState.currentTurnIndex,
            this.playerId
        );
        const wasMyTurn = this.isMyTurn;
        this.isMyTurn = moveStatus.canMove;

        // Play notification sound if it just became our turn (regular turn only)
        if (this.isMyTurn && !wasMyTurn && moveStatus.reason === MOVE_REASON.TURN) {
            this.playNotificationSound();
        }

        // Re-enable controls after submission completes (controls stay active for tile arrangement)
        this.controlsEl.classList.remove('disabled');

        // Show appropriate message based on reason
        // But don't overwrite swap mode instructions
        if (this.localGame && this.localGame.swapMode) {
            return;
        }

        const message = getReasonMessage(moveStatus.reason);
        if (moveStatus.canMove) {
            this.showMessage(message || "It's your turn!", 'success');
        } else {
            // For waiting, show who we're waiting for
            const currentPlayer = this.gameState.players[this.gameState.currentTurnIndex];
            if (currentPlayer && isPlayerActive(currentPlayer)) {
                this.showMessage(`Waiting for ${currentPlayer.nickname}...`, 'info');
            } else {
                this.showMessage(message || 'Waiting for players...', 'info');
            }
        }
    }

    renderPlayers() {
        this.playerListEl.innerHTML = '';

        this.gameState.players.forEach((player, index) => {
            const li = document.createElement('li');

            if (index === this.gameState.currentTurnIndex) {
                li.classList.add('current-turn');
            }
            if (player.id === this.playerId) {
                li.classList.add('you');
            }
            if (!isPlayerActive(player)) {
                li.classList.add('inactive');
            }
            if (player.isIdle) {
                li.classList.add('idle');
            }

            const nameSpan = document.createElement('span');
            nameSpan.className = 'player-name';
            nameSpan.textContent = player.nickname;

            const scoreSpan = document.createElement('span');
            scoreSpan.className = 'player-score';
            scoreSpan.textContent = player.score;

            const turnsSpan = document.createElement('span');
            turnsSpan.className = 'player-turns';
            turnsSpan.textContent = `(${player.turnsTaken || 0})`;

            li.appendChild(nameSpan);
            li.appendChild(scoreSpan);
            li.appendChild(turnsSpan);
            this.playerListEl.appendChild(li);
        });
    }

    renderMoveHistory() {
        this.moveHistoryEl.innerHTML = '';
        const moves = [...(this.gameState.moves || [])].reverse();

        for (const move of moves) {
            const li = document.createElement('li');

            // Create player name span (safely escaped via textContent)
            const playerSpan = document.createElement('span');
            playerSpan.className = 'move-player';
            playerSpan.textContent = move.nickname;

            if (move.type === 'pass' || move.type === 'snooze') {
                li.appendChild(playerSpan);
                li.appendChild(document.createTextNode(' '));
                const passSpan = document.createElement('span');
                passSpan.className = 'move-pass';
                passSpan.textContent = move.type === 'snooze' ? 'went away' : 'passed';
                li.appendChild(passSpan);
            } else if (move.type === 'swap') {
                li.appendChild(playerSpan);
                li.appendChild(document.createTextNode(' '));
                const swapSpan = document.createElement('span');
                swapSpan.className = 'move-pass';
                swapSpan.textContent = `swapped ${move.swapCount} tile${move.swapCount > 1 ? 's' : ''}`;
                li.appendChild(swapSpan);
            } else if (move.type === 'timeout') {
                li.appendChild(playerSpan);
                li.appendChild(document.createTextNode(' '));
                const timeoutSpan = document.createElement('span');
                timeoutSpan.className = 'move-pass';
                timeoutSpan.textContent = 'went auto-away';
                li.appendChild(timeoutSpan);
            } else if (move.type === 'return') {
                li.appendChild(playerSpan);
                li.appendChild(document.createTextNode(' '));
                const returnSpan = document.createElement('span');
                returnSpan.className = 'move-return';
                returnSpan.textContent = 'came back';
                li.appendChild(returnSpan);
            } else if (move.type === 'finish') {
                li.appendChild(playerSpan);
                li.appendChild(document.createTextNode(' '));
                const finishSpan = document.createElement('span');
                finishSpan.className = 'move-pass';
                finishSpan.textContent = 'finished';
                li.appendChild(finishSpan);
            } else if (move.type === 'bonus') {
                const bonusSpan = document.createElement('span');
                bonusSpan.className = 'move-score';
                bonusSpan.textContent = `joined +${move.score}`;

                li.appendChild(document.createTextNode('\uD83C\uDFA1 '));
                li.appendChild(playerSpan);
                li.appendChild(document.createTextNode(' '));
                li.appendChild(bonusSpan);
            } else {
                const words = (move.words || []).join(', ');
                li.appendChild(playerSpan);
                li.appendChild(document.createTextNode(': '));
                const wordsSpan = document.createElement('span');
                wordsSpan.className = 'move-words';
                wordsSpan.textContent = words;
                li.appendChild(wordsSpan);
                li.appendChild(document.createTextNode(' '));
                const scoreSpan = document.createElement('span');
                scoreSpan.className = 'move-score';
                scoreSpan.textContent = `+${move.score}`;
                li.appendChild(scoreSpan);

                // Show "finished" indicator for auto-finish moves
                if (move.autoFinish) {
                    const finishSpan = document.createElement('span');
                    finishSpan.className = 'move-pass';
                    finishSpan.textContent = ' (finished)';
                    li.appendChild(finishSpan);
                }
            }

            this.moveHistoryEl.appendChild(li);
        }
    }

    updateTileBagCounter() {
        const tilesRemainingEl = document.getElementById('tiles-remaining');
        if (tilesRemainingEl && this.gameState.tileBag) {
            tilesRemainingEl.textContent = this.gameState.tileBag.length;
        }
        const bagNameEl = document.getElementById('tile-bag-name');
        if (bagNameEl && this.gameState.tileBagName) {
            bagNameEl.textContent = this.gameState.tileBagName;
        }

        const bagEmpty = this.gameState.tileBag && this.gameState.tileBag.length === 0;
        const controlsStack = document.querySelector('.controls-stack');
        const finishButton = document.getElementById('finish-game');

        if (bagEmpty) {
            if (controlsStack) controlsStack.classList.add('hidden');
            if (finishButton) finishButton.classList.remove('hidden');
        } else {
            if (controlsStack) controlsStack.classList.remove('hidden');
            if (finishButton) finishButton.classList.add('hidden');
        }
    }

    async submitMove() {
        if (!this.isMyTurn) {
            this.showMessage("It's not your turn!", 'error');
            return;
        }

        if (this.isSubmitting) return;

        const pending = this.localGame.board.getPendingTiles();

        if (pending.length === 0) {
            this.showMessage('Place some tiles first!', 'error');
            return;
        }

        // Check blank tiles
        for (const { row, col } of pending) {
            const tile = this.localGame.board.getTileAt(row, col);
            if (tile && tile.isBlank && !tile.assignedLetter) {
                this.showMessage('Assign a letter to the blank tile!', 'error');
                return;
            }
        }

        // Validate placement
        const validation = this.localGame.validatePlacement(pending);
        if (!validation.valid) {
            this.showMessage(validation.message, 'error');
            return;
        }

        // Check first move
        if (this.gameState.isFirstMove) {
            const coversCenter = pending.some(p => this.localGame.board.isCenterCell(p.row, p.col));
            if (!coversCenter) {
                this.showMessage('First word must cover the center!', 'error');
                return;
            }
        }

        // Check connection
        if (!this.gameState.isFirstMove) {
            const isConnected = this.localGame.checkConnection(pending);
            if (!isConnected) {
                this.showMessage('Must connect to existing words!', 'error');
                return;
            }
        }

        // Find words
        const words = this.localGame.findAllWords(pending, validation.direction);
        if (words.length === 0) {
            this.showMessage('Words must be at least 2 letters.', 'error');
            return;
        }

        // Validate words
        const invalidWords = words.filter(w => !isValidWord(w.word));
        if (invalidWords.length > 0) {
            this.showMessage(`Invalid: ${invalidWords.map(w => w.word).join(', ')}`, 'error');
            return;
        }

        // Calculate score
        const totalScore = this.localGame.calculateScore(words);

        this.isSubmitting = true;
        this.controlsEl.classList.add('disabled');
        this.showMessage('Submitting...', 'info');

        try {
            const response = await fetch(`/.proxy/api/games/${this.gameCode}/move`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify({
                    nickname: this.nickname,
                    pending: pending.map(p => ({
                        row: p.row,
                        col: p.col,
                        tile: {
                            letter: p.tile.letter,
                            points: p.tile.points,
                            isBlank: p.tile.isBlank,
                            assignedLetter: p.tile.assignedLetter,
                            id: p.tile.id
                        }
                    })),
                    words,
                    score: totalScore,
                    moveId: `move_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
                    autoEndWhenAllIdle: flags.get('autoEndWhenAllIdle')
                })
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Failed to submit move');
            }

            const { game } = await response.json();
            this.gameState = game;
            this.onGameStateUpdate();

            const wordList = words.map(w => w.word).join(', ');
            this.showMessage(`+${totalScore}! ${wordList}`, 'success');
        } catch (error) {
            console.error('Failed to submit move:', error);
            this.showMessage(error.message || 'Failed to submit move', 'error');
        } finally {
            this.isSubmitting = false;
        }
    }

    async submitSwap() {
        if (!this.isMyTurn) {
            this.showMessage("It's not your turn!", 'error');
            this.localGame.exitSwapMode();
            return;
        }

        if (this.isSubmitting) return;

        const tileIds = Array.from(this.localGame.tilesMarkedForSwap);
        if (tileIds.length === 0) {
            this.localGame.exitSwapMode();
            return;
        }

        this.isSubmitting = true;
        this.controlsEl.classList.add('disabled');

        try {
            const response = await fetch(`/.proxy/api/games/${this.gameCode}/swap`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify({
                    nickname: this.nickname,
                    tileIds
                })
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Failed to swap');
            }

            const { game } = await response.json();
            this.gameState = game;
            this.onGameStateUpdate();
            this.localGame.exitSwapMode();
            this.showMessage(`Swapped ${tileIds.length} tile(s)`, 'success');
        } catch (error) {
            console.error('Failed to swap:', error);
            this.showMessage(error.message || 'Failed to swap', 'error');
            this.localGame.exitSwapMode();
        } finally {
            this.isSubmitting = false;
        }
    }

    async snooze() {
        if (this.isSubmitting) return;

        this.isSubmitting = true;

        try {
            const response = await fetch(`/.proxy/api/games/${this.gameCode}/snooze`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify({
                    nickname: this.nickname
                })
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Failed to snooze');
            }

            const { game } = await response.json();
            this.gameState = game;
            this.onGameStateUpdate();
            this.showMessage('You went away - click anywhere to return', 'info');
        } catch (error) {
            console.error('Failed to snooze:', error);
            this.showMessage(error.message || 'Failed to go away', 'error');
        } finally {
            this.isSubmitting = false;
        }
    }

    async comeBackFromAway() {
        // Don't run if already submitting or not actually idle
        if (this.isSubmitting || !this.isCurrentPlayerIdle()) {
            return;
        }

        this.isSubmitting = true;
        try {
            const response = await fetch(`/.proxy/api/games/${this.gameCode}/return`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify({
                    nickname: this.nickname
                })
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Failed to return');
            }

            const { game, alreadyActive } = await response.json();
            if (!alreadyActive) {
                this.gameState = game;
                this.onGameStateUpdate();
                this.showMessage("You're back! Play when ready.", 'info');
            }
        } catch (error) {
            console.error('Failed to return from away:', error);
            // Silent fail - don't bother user with error for auto-return
        } finally {
            this.isSubmitting = false;
        }
    }

    async finishGame() {
        // Finish can be called at any time (not just on your turn)
        if (this.isSubmitting) return;

        this.localGame.recallTiles();
        this.isSubmitting = true;
        this.controlsEl.classList.add('disabled');

        try {
            const response = await fetch(`/.proxy/api/games/${this.gameCode}/finish`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify({
                    nickname: this.nickname,
                    autoEndWhenAllIdle: flags.get('autoEndWhenAllIdle')
                })
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Failed to finish');
            }

            const { game } = await response.json();
            this.gameState = game;
            this.onGameStateUpdate();
            this.showMessage('You finished!', 'success');
        } catch (error) {
            console.error('Failed to finish:', error);
            this.showMessage(error.message || 'Failed to finish', 'error');
        } finally {
            this.isSubmitting = false;
        }
    }

    async leave() {
        try {
            await gameAPI.leaveGame(this.gameCode, this.playerId);
        } catch (error) {
            console.error('Failed to leave:', error);
        } finally {
            // Stop polling first
            this.cleanup();
            // Then clear the Discord Activity status (don't await, fire and forget)
            updateActivityState(null, null, 0).catch(() => {});
        }
    }

    shouldShowSoftGameOver() {
        const myPlayer = this.gameState.players.find(p => p.id === this.playerId);
        if (!myPlayer || !myPlayer.isFinished) return false;
        if (this.gameState.status === 'ended') return false;
        const activePlayers = this.gameState.players.filter(p => isPlayerActive(p));
        return activePlayers.length > 0 && activePlayers.every(p => p.isIdle);
    }

    showSoftGameOver() {
        this.softGameOverActive = true;
        this.controlsEl.classList.add('disabled');
        // Do NOT stop polling — we need updates when idle players return
        this.showScoreboard(
            [...this.gameState.players].sort((a, b) => b.score - a.score),
            [...this.gameState.players].sort((a, b) => b.score - a.score)[0],
            { soft: true }
        );
        const overlay = document.getElementById('game-scoreboard');
        if (overlay) {
            this.cleanupAmbientFireworks = startAmbientFireworks(overlay, this._blimpMessages());
        }
        // Full celebration (fanfare + big fireworks) immediately
        this.cleanupCelebration = celebrate();
    }

    refreshSoftScoreboard() {
        this.showScoreboard(
            [...this.gameState.players].sort((a, b) => b.score - a.score),
            [...this.gameState.players].sort((a, b) => b.score - a.score)[0],
            { soft: true, animate: false }
        );
    }

    upgradeSoftToTrueGameOver() {
        // Keep ambient fireworks running — just refresh the scoreboard
        // to remove soft-game-over elements (waiting note, away badges)
        this.softGameOverActive = false;
        const sortedPlayers = [...this.gameState.players].sort((a, b) => b.score - a.score);
        const winner = sortedPlayers[0];
        this.showScoreboard(sortedPlayers, winner, { animate: false });
        // Stop polling now that game is truly ended
        gameAPI.stopPolling();

        // Update Discord Activity status with winner info
        const playerCount = this.gameState.players ? this.gameState.players.length : 0;
        const gameEndInfo = {
            winner: { nickname: winner.nickname, score: winner.score },
            players: sortedPlayers.slice(0, 3).map(p => ({ nickname: p.nickname, score: p.score }))
        };
        updateActivityState('Game Over', this.gameCode, playerCount, 5, gameEndInfo);
    }

    showGameOver() {
        const sortedPlayers = [...this.gameState.players].sort((a, b) => b.score - a.score);
        const winner = sortedPlayers[0];
        this.showScoreboard(sortedPlayers, winner);
        this.controlsEl.classList.add('disabled');
        gameAPI.stopPolling();

        // Fireworks + fanfare
        this.cleanupCelebration = celebrate();

        // Persistent ambient fireworks on the scoreboard background
        const overlay = document.getElementById('game-scoreboard');
        if (overlay) {
            this.cleanupAmbientFireworks = startAmbientFireworks(overlay, this._blimpMessages());
        }

        // Update Discord Activity status with winner info
        const playerCount = this.gameState.players ? this.gameState.players.length : 0;
        const gameEndInfo = {
            winner: {
                nickname: winner.nickname,
                score: winner.score
            },
            players: sortedPlayers.slice(0, 3).map(p => ({
                nickname: p.nickname,
                score: p.score
            }))
        };
        updateActivityState('Game Over', this.gameCode, playerCount, 5, gameEndInfo);
    }

    _blimpMessages() {
        const moves = this.gameState.moves || [];
        const playMoves = moves.filter(m => m.type === 'play');
        const bestMove = playMoves.length > 0
            ? playMoves.reduce((best, m) => m.score > best.score ? m : best, playMoves[0])
            : null;

        const msgs = [];
        if (bestMove) {
            const word = bestMove.words?.[0]?.word;
            msgs.push(`Best play: ${word ? word.toUpperCase() + ' ' : ''}+${bestMove.score} by ${bestMove.nickname}`);
        }
        return msgs;
    }

    showScoreboard(sortedPlayers, winner, options = {}) {
        const { soft = false, animate = true } = options;

        // Reuse existing overlay if present (preserves ambient fireworks canvas)
        let overlay = document.getElementById('game-scoreboard');
        if (overlay) {
            const existingModal = overlay.querySelector('.scoreboard-modal');
            if (existingModal) existingModal.remove();
        } else {
            overlay = document.createElement('div');
            overlay.id = 'game-scoreboard';
            overlay.className = 'scoreboard-overlay';
        }

        const medals = ['🥇', '🥈', '🥉'];

        const modal = document.createElement('div');
        modal.className = 'scoreboard-modal';
        if (!animate) {
            modal.classList.add('no-animate');
        }

        // Title
        const title = document.createElement('h2');
        title.className = 'scoreboard-title';
        title.textContent = 'Game Over!';
        modal.appendChild(title);

        // Winner (using textContent to prevent XSS)
        const winnerDiv = document.createElement('div');
        winnerDiv.className = 'scoreboard-winner';
        winnerDiv.textContent = `🏆 ${winner.nickname} wins!`;
        modal.appendChild(winnerDiv);

        // Player list
        const list = document.createElement('ul');
        list.className = 'scoreboard-list';

        sortedPlayers.forEach((player, i) => {
            const li = document.createElement('li');
            li.className = 'scoreboard-player';
            if (player.id === this.playerId) {
                li.classList.add('you');
            }

            // Medal for top 3
            if (i < 3) {
                const medal = document.createElement('span');
                medal.className = 'scoreboard-medal';
                medal.textContent = medals[i];
                li.appendChild(medal);
            }

            // Show rank number for players outside top 3 (medals cover top 3)
            if (i >= 3) {
                const rankSpan = document.createElement('span');
                rankSpan.className = 'scoreboard-rank';
                rankSpan.textContent = `${i + 1}.`;
                li.appendChild(rankSpan);
            }

            const nameSpan = document.createElement('span');
            nameSpan.className = 'scoreboard-name';
            nameSpan.textContent = player.nickname;

            // Away badge for idle players in soft game over
            if (soft && isPlayerActive(player) && player.isIdle) {
                const awayBadge = document.createElement('span');
                awayBadge.className = 'scoreboard-away';
                awayBadge.textContent = 'Away';
                nameSpan.appendChild(awayBadge);
            }

            li.appendChild(nameSpan);

            const scoreSpan = document.createElement('span');
            scoreSpan.className = 'scoreboard-score';
            scoreSpan.textContent = player.score === 0 ? '🥚' : player.score;
            li.appendChild(scoreSpan);

            const avgSpan = document.createElement('span');
            avgSpan.className = 'scoreboard-avg';
            const avgScore = player.wordsPlayed > 0 ? (player.score / player.wordsPlayed).toFixed(1) : '0';
            avgSpan.textContent = `(${avgScore}/play)`;
            li.appendChild(avgSpan);

            list.appendChild(li);
        });

        modal.appendChild(list);

        // Waiting note for soft game over
        if (soft) {
            const waitingNote = document.createElement('div');
            waitingNote.className = 'scoreboard-waiting-note';
            waitingNote.textContent = 'Away players can still return and play';
            modal.appendChild(waitingNote);
        }

        // Game stats
        const moves = this.gameState.moves || [];
        const playMoves = moves.filter(m => m.type === 'play');
        const totalWords = playMoves.reduce((sum, m) => sum + (m.words?.length || 0), 0);
        const bestMove = playMoves.length > 0
            ? playMoves.reduce((best, m) => m.score > best.score ? m : best, playMoves[0])
            : null;

        if (bestMove) {
            const statsRow = document.createElement('div');
            statsRow.className = 'scoreboard-stats';

            const totalStat = document.createElement('div');
            totalStat.className = 'scoreboard-stat';
            const totalVal = document.createElement('span');
            totalVal.className = 'scoreboard-stat-value';
            totalVal.textContent = totalWords;
            const totalLabel = document.createElement('span');
            totalLabel.className = 'scoreboard-stat-label';
            totalLabel.textContent = 'Words';
            totalStat.appendChild(totalVal);
            totalStat.appendChild(totalLabel);

            const bestStat = document.createElement('div');
            bestStat.className = 'scoreboard-stat';
            const bestVal = document.createElement('span');
            bestVal.className = 'scoreboard-stat-value';
            bestVal.textContent = `+${bestMove.score}`;
            const bestLabel = document.createElement('span');
            bestLabel.className = 'scoreboard-stat-label';
            bestLabel.textContent = 'Best Play';
            bestStat.appendChild(bestVal);
            bestStat.appendChild(bestLabel);
            bestStat.title = bestMove.nickname;

            const turnsStat = document.createElement('div');
            turnsStat.className = 'scoreboard-stat';
            const turnsVal = document.createElement('span');
            turnsVal.className = 'scoreboard-stat-value';
            turnsVal.textContent = playMoves.length;
            const turnsLabel = document.createElement('span');
            turnsLabel.className = 'scoreboard-stat-label';
            turnsLabel.textContent = 'Turns';
            turnsStat.appendChild(turnsVal);
            turnsStat.appendChild(turnsLabel);

            statsRow.appendChild(totalStat);
            statsRow.appendChild(bestStat);
            statsRow.appendChild(turnsStat);
            modal.appendChild(statsRow);
        }

        const backBtn = document.createElement('button');
        backBtn.className = 'scoreboard-btn';
        backBtn.textContent = 'Back to Menu';
        backBtn.addEventListener('click', () => {
            overlay.remove();
            if (this.cleanupAmbientFireworks) {
                this.cleanupAmbientFireworks();
                this.cleanupAmbientFireworks = null;
            }
            if (this.cleanupCelebration) this.cleanupCelebration();
            this.softGameOverActive = false;
            this.cleanup();
            window.app.showScreen('menu');
        });
        modal.appendChild(backBtn);

        overlay.appendChild(modal);
        if (!overlay.parentNode) {
            document.body.appendChild(overlay);
        }
    }

    showMessage(text, type) {
        const MIN_DISPLAY_MS = 3000; // Minimum 3 seconds display time
        const now = Date.now();

        // Don't let info messages overwrite error messages too quickly
        // But success messages can always override (they indicate the error was resolved)
        if (type === 'info') {
            if (this.errorMessageUntil && now < this.errorMessageUntil) {
                return; // Don't overwrite recent error
            }
        }

        // Check if current message needs more display time
        if (type !== 'error' && this.messageShownAt && now < this.messageShownAt + MIN_DISPLAY_MS) {
            // Cancel any existing pending message
            if (this.pendingMessageTimer) {
                clearTimeout(this.pendingMessageTimer);
            }
            // Schedule this message for later
            const delay = this.messageShownAt + MIN_DISPLAY_MS - now;
            this.pendingMessageTimer = setTimeout(() => {
                this.pendingMessageTimer = null;
                this.showMessage(text, type);
            }, delay);
            return;
        }

        // Clear any pending message since we're showing a new one now
        if (this.pendingMessageTimer) {
            clearTimeout(this.pendingMessageTimer);
            this.pendingMessageTimer = null;
        }

        this.messageEl.textContent = text;
        this.messageEl.className = 'message ' + type;
        this.messageShownAt = now;

        // Error messages get protection from being overwritten by info messages
        if (type === 'error') {
            this.errorMessageUntil = now + 5000; // 5 seconds
        } else if (type === 'success') {
            this.errorMessageUntil = null; // Clear error protection on success
        }
    }

    cleanup() {
        // Disconnect flag sync
        flags.onFlagChanged = null;

        gameAPI.stopPolling();
        // Clear pending message timer
        if (this.pendingMessageTimer) {
            clearTimeout(this.pendingMessageTimer);
            this.pendingMessageTimer = null;
        }
        // Clean up ambient fireworks
        if (this.cleanupAmbientFireworks) {
            this.cleanupAmbientFireworks();
            this.cleanupAmbientFireworks = null;
        }
        this.softGameOverActive = false;
        // Remove activity listeners
        if (this.activityHandler && this.activityContainer) {
            this.activityContainer.removeEventListener('click', this.activityHandler);
            this.activityContainer.removeEventListener('keydown', this.activityHandler);
            this.activityHandler = null;
            this.activityContainer = null;
        }
        if (this.localGame) {
            this.localGame.destroy();
            this.localGame = null;
        }
    }
}
